<script setup lang="ts">
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import AppSegmentedControl from '../../primitives/AppSegmentedControl/AppSegmentedControl.vue'
import type { SegmentOption } from '../../primitives/AppSegmentedControl/segmented-control-types'
import type { AppPageTab } from './app-page-types'

const props = defineProps<{
  /** The large `<h1>` under the trail on a detail page; omit where the title lives elsewhere. */
  title?: string
  /** Shows the description: as the line under `title` when one is set, else as a top-level page's lead. */
  showDescription: boolean
  description?: string
  tabs?: readonly AppPageTab[]
  tabsAriaLabel?: string
  activeTab?: string
}>()

defineSlots<{
  description?: () => unknown
  meta?: () => unknown
  /** Controls beside the title; they drop under it while the page is narrow. */
  actions?: () => unknown
}>()

const route = useRoute()
const router = useRouter()

function tabRouteName(to: RouteLocationRaw): string | undefined {
  return typeof to === 'object' && 'name' in to && to.name !== undefined
    ? String(to.name)
    : undefined
}

const tabOptions = computed<SegmentOption[]>(
  () =>
    props.tabs?.map((tab) => ({
      label: tab.label,
      value: tab.id,
      badge: tab.badge,
      badgeTone: tab.badgeTone,
    })) ?? [],
)

const activeTabId = computed<string>(
  () => props.activeTab ?? props.tabs?.find((tab) => tabRouteName(tab.to) === route.name)?.id ?? '',
)

function onTabChange(value: string): void {
  const tab = props.tabs?.find((entry) => entry.id === value)
  if (tab) void router.push(tab.to)
}
</script>

<template>
  <div class="flex flex-col">
    <div
      v-if="title"
      class="flex min-w-0 flex-col gap-8 @article-split:flex-row @article-split:items-start @article-split:justify-between"
    >
      <div class="flex min-w-0 flex-col">
        <h1 class="text-h2 text-text-primary">{{ title }}</h1>
        <p
          v-if="showDescription"
          class="mt-4 max-w-(--message-max-width) text-body text-text-secondary"
        >
          <slot name="description">{{ description }}</slot>
        </p>
        <div
          v-if="$slots.meta"
          class="mt-4 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 text-body-sm text-text-secondary"
        >
          <slot name="meta" />
        </div>
      </div>
      <div v-if="$slots.actions" class="flex shrink-0 items-center gap-4">
        <slot name="actions" />
      </div>
    </div>
    <p
      v-else-if="showDescription"
      class="max-w-(--message-max-width) text-body-sm text-text-secondary"
    >
      <slot name="description">{{ description }}</slot>
    </p>
    <AppSegmentedControl
      v-if="tabs?.length"
      class="self-start not-first:mt-8"
      :options="tabOptions"
      :model-value="activeTabId"
      :aria-label="tabsAriaLabel"
      @update:model-value="onTabChange"
    />
  </div>
</template>
